import React, { FormEvent, useCallback, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import type { TagType } from '~/consts/tags';

import { downloadCsvFile } from '~/utils/csvDownload';

import { runBulkTagMutation } from './bulkTagAdapters';
import { getBulkTagAdapterConfig } from './bulkTagTypes';
import type { BulkActionResult, HotelBulkTagTarget } from './bulkTagTypes';
import { parseOfferIds } from './parseOfferIds';
import { isParseOfferIdsCsvFailure, parseOfferIdsCsv, readCsvFile } from './parseOfferIdsCsv';
import type { MarketingVerticalConfig } from './verticalConfig';

type Props = {
  tags: Array<App.MarketingTag>;
  tagType: TagType;
  vertical: MarketingVerticalConfig;
};

const DEFAULT_CSV_HEADERS = ['offer_id'];
const BEDBANK_CSV_HEADERS = ['property_id'];

const HOTEL_TARGET_OPTIONS: Array<{
  value: HotelBulkTagTarget;
  label: string;
  helperText: string;
  idLabel: string;
  placeholder: string;
  csvTemplateExampleIds: Array<string>;
  csvHeaders: Array<string>;
}> = [
  {
    value: 'svc-offer',
    label: 'Flash / LPC offers',
    helperText: 'Use Flash, last-minute hotel, or LPC offer IDs from svc-offer.',
    idLabel: 'Offer IDs',
    placeholder: 'Enter flash/LPC offer IDs (e.g. OFF123), separated by commas or new lines',
    csvTemplateExampleIds: ['OFF123'],
    csvHeaders: DEFAULT_CSV_HEADERS,
  },
  {
    value: 'bedbank',
    label: 'Bedbank properties',
    helperText: 'Use Bedbank master or supplier property UUIDs.',
    idLabel: 'Property IDs',
    placeholder: 'Enter bedbank property UUIDs, separated by commas or new lines',
    csvTemplateExampleIds: ['a1b2c3d4-e5f6-7890-abcd-ef1234567890'],
    csvHeaders: BEDBANK_CSV_HEADERS,
  },
];

function looksLikeOfferId(value: string): boolean {
  return /^[a-zA-Z0-9]{15,18}$/.test(value);
}

function titleCase(value: string) {
  return value.replace(/^\w/, (char) => char.toUpperCase());
}

function formatBulkSummary(
  result: BulkActionResult,
  itemLabelSingular: string,
  itemLabelPlural: string,
  mode: BulkActionResult['mode'],
) {
  const appliedCount = result.success.length;
  const unchangedCount = result.unchanged.length;
  const failureCount = result.failures.length;
  const verb = mode === 'apply' ? 'Applied' : 'Removed';

  const parts = [`${verb} tags on ${appliedCount} ${appliedCount === 1 ? itemLabelSingular : itemLabelPlural}.`];

  if (unchangedCount > 0) {
    parts.push(
      `${unchangedCount} ${unchangedCount === 1 ? `${itemLabelSingular} was` : `${itemLabelPlural} were`} already ${
        mode === 'apply' ? 'tagged' : 'untagged'
      }.`,
    );
  }

  if (failureCount > 0) {
    parts.push(`${failureCount} ${failureCount === 1 ? 'ID was' : 'IDs were'} not found or could not be updated.`);
  }

  return parts.join(' ');
}

export default function BulkTagApply({ tags, tagType, vertical }: Props) {
  const [selectedTags, setSelectedTags] = useState<Array<App.MarketingTag>>([]);
  const [offerIdsInput, setOfferIdsInput] = useState<string>('');
  const [hotelTarget, setHotelTarget] = useState<HotelBulkTagTarget>('svc-offer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkActionResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [csvMessage, setCsvMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isHotels = vertical.id === 'hotels';
  const hotelTargetOption = useMemo(
    () => HOTEL_TARGET_OPTIONS.find((option) => option.value === hotelTarget) ?? HOTEL_TARGET_OPTIONS[0],
    [hotelTarget],
  );

  const config = useMemo(
    () =>
      getBulkTagAdapterConfig(tagType, vertical.label, {
        supportsPropertyIds: isHotels && hotelTarget === 'bedbank',
      }),
    [hotelTarget, isHotels, tagType, vertical.label],
  );
  const bulkTitle =
    isHotels && hotelTarget === 'bedbank' ? `Bulk apply ${config.labelPlural} to Bedbank properties` : config.title;
  const itemLabelSingular = isHotels && hotelTarget === 'bedbank' ? 'property' : 'offer';
  const itemLabelPlural = isHotels && hotelTarget === 'bedbank' ? 'properties' : 'offers';
  const tagOptions = useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags]);
  const tagNameLookup = useMemo(() => {
    const lookup = new Map<string, App.MarketingTag>();
    tagOptions.forEach((tag) => {
      lookup.set(tag.name.toLocaleLowerCase(), tag);
    });
    return lookup;
  }, [tagOptions]);

  const handleTagPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const pastedText = event.clipboardData?.getData('text') ?? '';

      if (!pastedText || !/[\n,\r]/.test(pastedText)) {
        return;
      }

      const pastedNames = pastedText
        .split(/\r?\n|,/)
        .map((value) => value.trim())
        .filter(Boolean);

      if (!pastedNames.length) {
        return;
      }

      let shouldPreventDefault = false;

      setSelectedTags((previous) => {
        const existingIds = new Set(previous.map((tag) => tag.id));
        const appendedTags: Array<App.MarketingTag> = [];

        pastedNames.forEach((name) => {
          const lookupKey = name.toLocaleLowerCase();
          const matchedTag = tagNameLookup.get(lookupKey);

          if (!matchedTag || existingIds.has(matchedTag.id)) {
            return;
          }

          appendedTags.push(matchedTag);
          existingIds.add(matchedTag.id);
        });

        if (!appendedTags.length) {
          return previous;
        }

        shouldPreventDefault = true;
        return [...previous, ...appendedTags];
      });

      if (shouldPreventDefault) {
        event.preventDefault();
      }
    },
    [tagNameLookup],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await runBulkAction('apply');
  };

  const runBulkAction = async (mode: 'apply' | 'remove') => {
    if (isSubmitting) {
      return;
    }

    if (!selectedTags.length) {
      setFormError(
        `Select at least one ${mode === 'apply' ? config.labelSingular : config.labelPlural} to ${
          mode === 'apply' ? 'apply' : 'remove'
        }.`,
      );
      return;
    }

    const offerIds = parseOfferIds(offerIdsInput);

    if (!offerIds.length) {
      setFormError(`Enter at least one ${isHotels && hotelTarget === 'bedbank' ? 'property' : 'offer'} ID.`);
      return;
    }

    const selectedTagNames = selectedTags.map((tag) => tag.name);
    const tagsMatchingOfferIds = selectedTagNames.filter((tagName) => offerIds.includes(tagName));

    if (tagsMatchingOfferIds.length) {
      setFormError(
        `"${tagsMatchingOfferIds.join('", "')}" appears in both ${config.labelPlural} and Offer IDs. Select tag names from the dropdown only, and put offer IDs in the Offer IDs field.`,
      );
      return;
    }

    const suspiciousTags = selectedTagNames.filter(looksLikeOfferId);

    if (suspiciousTags.length) {
      setFormError(
        `"${suspiciousTags.join('", "')}" looks like an offer ID, not a ${config.labelSingular}. Choose a tag name from the dropdown (e.g. "Best of 2024") and enter the offer ID below.`,
      );
      return;
    }

    setFormError(null);
    setCsvMessage(null);
    setIsSubmitting(true);
    setResult(null);

    try {
      const bulkResult = await runBulkTagMutation({
        verticalId: vertical.id,
        tagType,
        mode,
        selectedTagNames,
        offerIds,
        catalogTagNames: tagOptions.map((tag) => tag.name),
        ...(isHotels ? { hotelTarget } : {}),
      });
      setResult(bulkResult);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvHeaders = isHotels ? hotelTargetOption.csvHeaders : DEFAULT_CSV_HEADERS;
    const idKey = csvHeaders[0];
    const exampleIds = isHotels ? hotelTargetOption.csvTemplateExampleIds : vertical.csvTemplateExampleIds;
    const rows = exampleIds.map((id) => ({ [idKey]: id }));
    downloadCsvFile(rows, csvHeaders, `${vertical.id}-${idKey.replace('_', '-')}-template.csv`);
  };

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setCsvMessage(null);
    setFormError(null);

    try {
      const fileContent = await readCsvFile(file);
      const parsed = parseOfferIdsCsv(fileContent);

      if (isParseOfferIdsCsvFailure(parsed)) {
        setFormError(parsed.error);
        return;
      }

      setOfferIdsInput(parsed.offerIds.join('\n'));
      setCsvMessage(
        `Loaded ${parsed.offerIds.length} ${itemLabelSingular} ${parsed.offerIds.length === 1 ? 'ID' : 'IDs'} from CSV.`,
      );
    } catch (error: unknown) {
      setFormError(error instanceof Error ? error.message : 'Could not upload the CSV file.');
    }
  };

  return (
    <Box component="section">
      <Typography variant="h6" mb={2}>
        {bulkTitle}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {isHotels && (
            <FormControl disabled={isSubmitting}>
              <FormLabel>Hotel target</FormLabel>
              <RadioGroup
                row
                value={hotelTarget}
                onChange={(event) => {
                  setHotelTarget(event.target.value as HotelBulkTagTarget);
                  setOfferIdsInput('');
                  setResult(null);
                  setFormError(null);
                  setCsvMessage(null);
                }}
              >
                {HOTEL_TARGET_OPTIONS.map((option) => (
                  <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
                ))}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                {hotelTargetOption.helperText}
              </Typography>
            </FormControl>
          )}

          <Autocomplete
            multiple
            freeSolo={false}
            clearOnBlur
            handleHomeEndKeys
            options={tagOptions}
            getOptionLabel={(option) => option.name}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value.filter((tag): tag is App.MarketingTag => Boolean(tag?.id)))}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) {
                return option === value;
              }
              return option.id === value.id;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={titleCase(config.labelPlural)}
                placeholder={`Select ${config.labelPlural} by name`}
                inputProps={{
                  ...params.inputProps,
                  onPaste: (event) => {
                    handleTagPaste(event);
                    if (!event.defaultPrevented) {
                      params.inputProps?.onPaste?.(event as React.ClipboardEvent<HTMLInputElement>);
                    }
                  },
                }}
              />
            )}
            disabled={isSubmitting}
            limitTags={4}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              {isHotels ? hotelTargetOption.idLabel : 'Offer IDs'}
            </Typography>
            <Button type="button" variant="outlined" size="small" onClick={handleDownloadTemplate}>
              Download template
            </Button>
            <Button variant="outlined" size="small" component="label" disabled={isSubmitting}>
              Upload CSV
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleCsvUpload} />
            </Button>
          </Stack>

          <TextField
            label={isHotels ? hotelTargetOption.idLabel : 'Offer IDs'}
            placeholder={isHotels ? hotelTargetOption.placeholder : vertical.offerIdPlaceholder}
            helperText={config.helperText}
            value={offerIdsInput}
            onChange={(event) => setOfferIdsInput(event.target.value)}
            multiline
            minRows={3}
            disabled={isSubmitting}
            required
          />

          {csvMessage && (
            <Alert severity="info" onClose={() => setCsvMessage(null)}>
              {csvMessage}
            </Alert>
          )}

          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Applying…' : 'Apply tags'}
            </Button>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isSubmitting}
              onClick={() => runBulkAction('remove')}
            >
              {isSubmitting ? 'Working…' : 'Remove tags'}
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={isSubmitting}
              onClick={() => {
                setSelectedTags([]);
                setOfferIdsInput('');
                setResult(null);
                setFormError(null);
                setCsvMessage(null);
              }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </form>

      {result && (
        <Box mt={3}>
          <Stack spacing={2}>
            {(result.success.length > 0 || result.unchanged.length > 0) && (
              <Alert severity="success">
                {formatBulkSummary(result, itemLabelSingular, itemLabelPlural, result.mode)}
                {result.success.length > 0 && result.success.length <= 20 && (
                  <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                    {result.success.map((entry) => (
                      <li key={entry.offerId}>
                        <Typography component="span" variant="body2">
                          {entry.offerId} –{' '}
                          {result.mode === 'apply'
                            ? `added ${entry.changedTags.join(', ')}`
                            : `removed ${entry.changedTags.join(', ')}`}
                          {entry.strippedTags?.length
                            ? ` (removed legacy invalid tags: ${entry.strippedTags.join(', ')})`
                            : ''}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {result.failures.length > 0 && (
              <Alert severity="error">
                {result.failures.length} {result.failures.length === 1 ? 'ID' : 'IDs'} not found or could not be
                updated:
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                  {result.failures.slice(0, 50).map((failure) => (
                    <li key={failure.offerId}>
                      <Typography component="span" variant="body2">
                        {failure.offerId} – {failure.reason}
                      </Typography>
                    </li>
                  ))}
                </Box>
                {result.failures.length > 50 && (
                  <Typography variant="body2" mt={1}>
                    …and {result.failures.length - 50} more.
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
